import { Box, Typography } from '@mui/material'
import { useMemo } from 'react'
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import type { FinancingChartProps } from './FinancingChart.types'

// Helper function to format large numbers for axis (e.g., 10000 -> 10k)
const formatAxisNumbers = (value: number): string => {
  if (value >= 1000) {
    return `${(value / 1000).toFixed(0)}k`
  }
  return value.toFixed(0)
}

// Helper function to format price in euros
const formatPrice = (euros: number): string => {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(euros)
}

// Chart data type
type ChartDataPoint = {
  year: number
  energyCosts: number
  energyCostsAppliedRetrofit: number
  annuity: number
  accumulatedEnergyCosts: number
  accumulatedEnergyCostsAppliedRetrofit: number
  accumulatedAnnuity: number
}

// Custom tooltip component
type CustomTooltipProps = {
  active?: boolean
  payload?: Array<{
    value: number
    payload: ChartDataPoint
  }>
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (active && payload && payload[0]) {
    const data = payload[0].payload

    return (
      <Box
        sx={{
          padding: '8px',
          background: '#ffffff',
          borderRadius: '8px',
          boxShadow: '0px 3px 9px 0px rgba(0, 0, 0, 0.18)',
          fontSize: '11px',
          width: '300px',
        }}
      >
        <Typography
          sx={{
            marginBottom: '4px',
            fontWeight: 700,
            color: '#6a707e',
            fontSize: '12px',
          }}
        >
          {data.year}
        </Typography>

        {/* Energiekosten unsaniert */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#366192',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten unsaniert:</span>
          <span>{formatPrice(data.energyCosts)}</span>
        </Box>

        {/* Energiekosten saniert */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#0E8C4A',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten saniert:</span>
          <span>{formatPrice(data.energyCostsAppliedRetrofit)}</span>
        </Box>

        {/* Finanzierungskosten */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#A48105',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Finanzierungskosten:</span>
          <span>{formatPrice(data.annuity ?? 0)}</span>
        </Box>

        {/* Einsparung */}
        <Box
          sx={{
            color: '#181d29',
            paddingTop: '4px',
            marginTop: '4px',
            borderTop: '1px solid #74777f',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
            fontWeight: 600,
          }}
        >
          <span>Einsparung:</span>
          <span>
            {formatPrice(data.energyCosts - data.energyCostsAppliedRetrofit - (data.annuity ?? 0))}
          </span>
        </Box>

        {/* Akkumuliert section */}
        <Typography
          sx={{
            marginBottom: '4px',
            marginTop: '12px',
            fontWeight: 700,
            color: '#6a707e',
            fontSize: '12px',
          }}
        >
          Akkumuliert
        </Typography>

        {/* Energiekosten unsaniert (akk.) */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#366192',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten unsaniert (akk.):</span>
          <span>{formatPrice(data.accumulatedEnergyCosts)}</span>
        </Box>

        {/* Energiekosten saniert (akk.) */}
        <Box
          sx={{
            fontWeight: 500,
            color: '#0E8C4A',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Energiekosten saniert (akk.):</span>
          <span>{formatPrice(data.accumulatedEnergyCostsAppliedRetrofit)}</span>
        </Box>

        {/* Finanzierungskosten (akk.) */}
        <Box
          sx={{
            color: '#A48105',
            marginTop: '4px',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
          }}
        >
          <span>Finanzierungskosten (akk.):</span>
          <span>{formatPrice(data.accumulatedAnnuity)}</span>
        </Box>

        {/* Bisher gespart */}
        <Box
          sx={{
            color: '#181d29',
            paddingTop: '4px',
            marginTop: '4px',
            borderTop: '1px solid #74777f',
            fontSize: '12px',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '4px',
            fontWeight: 600,
          }}
        >
          <span>Bisher gespart:</span>
          <span>
            {formatPrice(
              data.accumulatedEnergyCosts -
                data.accumulatedEnergyCostsAppliedRetrofit -
                data.accumulatedAnnuity
            )}
          </span>
        </Box>
      </Box>
    )
  }
  return null
}

const FinancingChart = ({ energyCostsResults, selectedFinancingResult }: FinancingChartProps) => {
  // Process data for chart
  const chartData = useMemo(() => {
    let accumulatedEnergyCosts = 0
    let accumulatedEnergyCostsAppliedRetrofit = 0
    let accumulatedAnnuity = 0
    const { annuityInCt: perYearAnnuityInCt, loanPeriod } = selectedFinancingResult

    return energyCostsResults.map((item, index) => {
      // Calculate annuity for this year (only during loan period)
      let thisYearAnnuity = 0
      if (index < loanPeriod) {
        thisYearAnnuity = Math.round(perYearAnnuityInCt / 100)
      }

      // Convert from cents to euros and round
      const energyCosts = Math.round(item.energyCostsInCt / 100)
      const energyCostsAppliedRetrofit = Math.round(item.energyCostsAppliedRetrofitInCt / 100)

      // Accumulate values
      accumulatedAnnuity += thisYearAnnuity
      accumulatedEnergyCostsAppliedRetrofit += energyCostsAppliedRetrofit
      accumulatedEnergyCosts += energyCosts

      return {
        year: item.year,
        energyCosts,
        energyCostsAppliedRetrofit,
        ...(thisYearAnnuity ? { annuity: thisYearAnnuity } : {}),
        accumulatedEnergyCosts,
        accumulatedEnergyCostsAppliedRetrofit,
        accumulatedAnnuity,
      }
    })
  }, [energyCostsResults, selectedFinancingResult])

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          width: '100%',
          height: '400px',
          '& *:focus': {
            outline: 'none',
          },
          '& svg': {
            outline: 'none',
          },
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={chartData}
            margin={{
              top: 12,
              right: 0,
              left: 0,
              bottom: 0,
            }}
          >
            <CartesianGrid strokeDasharray="1 0" vertical={false} stroke="#E1E1E4" />
            <XAxis
              strokeWidth={0}
              dataKey="year"
              tick={{ fill: '#74777F', fontSize: 12 }}
              tickLine={false}
            />
            <YAxis
              strokeWidth={0}
              width={50}
              tickFormatter={formatAxisNumbers}
              tick={{ fill: '#74777F', fontSize: 12 }}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />

            {/* Energiekosten unsaniert - blue */}
            <Area
              type="monotone"
              dataKey="energyCosts"
              stroke="#5084C9"
              fill="#6BA7FF33"
              strokeWidth={2}
            />

            {/* Energiekosten saniert - stacked, no border */}
            <Area
              type="monotone"
              dataKey="energyCostsAppliedRetrofit"
              stroke="none"
              fill="#BAF0E8"
              stackId="1"
              strokeWidth={0}
            />

            {/* Finanzierungskosten - yellow, stacked */}
            <Area
              type="monotone"
              dataKey="annuity"
              stroke="#5BC06B"
              fill="#EDC74CCC"
              stackId="1"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  )
}

export default FinancingChart
